import React from 'react';
import {
  Drawer, Box, Typography, IconButton, Divider, Button, Stack,
} from '@mui/material';
import { Close, Add, Remove, DeleteOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../store/cart';
import { useLang } from '../i18n/LanguageProvider';

const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')} EUR`;

interface Props {
  open: boolean;
  onClose: () => void;
}

export const CartDrawer: React.FC<Props> = ({ open, onClose }) => {
  const { lines, subtotal, setQuantity, remove, clear } = useCart();
  const navigate = useNavigate();
  const { t } = useLang();

  // MUI's Drawer auto-flips left/right based on theme.direction
  // (verified in node_modules/@mui/material/Drawer/Drawer.js:
  //   `direction === 'rtl' ? oppositeDirection[anchor] : anchor`).
  // So anchor='right' renders on the visual right in LTR and on the
  // visual left in RTL — exactly what we want. No conditional needed.
  const checkout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          background: 'linear-gradient(180deg, #0A2540 0%, #050B14 100%)',
          color: '#fff',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>{t('cart.title')}</Typography>
        <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="close cart">
          <Close />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,200,150,0.15)' }} />

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {lines.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.6)' }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('cart.empty')}</Typography>
            <Typography sx={{ fontSize: '13px' }}>{t('cart.empty_sub')}</Typography>
          </Box>
        )}

        {lines.map((line) => (
          <Box key={line.menu_item_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography dir="auto" sx={{ fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', unicodeBidi: 'isolate' }}>
                {line.name}
              </Typography>
              <Typography sx={{ color: '#00C896', fontSize: '13px', fontWeight: 600 }}>
                {fmt(line.unit_price)}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                aria-label="decrement"
                sx={{ color: '#00C896', backgroundColor: 'rgba(0,200,150,0.1)', '&:hover': { backgroundColor: 'rgba(0,200,150,0.2)' } }}
                onClick={() => setQuantity(line.menu_item_id, line.quantity - 1)}
              >
                <Remove fontSize="small" />
              </IconButton>
              <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{line.quantity}</Typography>
              <IconButton
                size="small"
                aria-label="increment"
                sx={{ color: '#00C896', backgroundColor: 'rgba(0,200,150,0.1)', '&:hover': { backgroundColor: 'rgba(0,200,150,0.2)' } }}
                onClick={() => setQuantity(line.menu_item_id, line.quantity + 1)}
              >
                <Add fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="remove"
                sx={{ color: 'rgba(255,255,255,0.5)' }}
                onClick={() => remove(line.menu_item_id)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        ))}
      </Box>

      {lines.length > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(0,200,150,0.15)', backgroundColor: 'rgba(10,37,64,0.6)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>{t('cart.subtotal')}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#00C896' }}>{fmt(subtotal)}</Typography>
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={checkout}
            sx={{
              py: 1.25,
              background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
              color: '#0A2540',
              fontWeight: 700,
              fontSize: '15px',
              '&:hover': { background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)' },
            }}
          >
            {t('cart.checkout')}
          </Button>
          <Button
            fullWidth
            onClick={clear}
            sx={{ mt: 1, color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}
          >
            {t('cart.clear')}
          </Button>
        </Box>
      )}
    </Drawer>
  );
};
